import { Component, type ReactNode } from 'react';

/**
 * Sprint BG-DataSettings 17 — PageErrorBoundary (旧 `old/apps/user/src/components/PageErrorBoundary.tsx` 踏襲、L3)。
 *
 * 全 page を包む error boundary。子 component で uncaught な例外が出ても white screen にせず、
 * エラー詳細 + 「再試行」「ホームへ」action を表示してユーザー / 管理者が原因を把握できるようにする。
 *
 * v2 改善点 (旧との差分):
 *   - shared-ui に配置 (旧は apps/user/components 専有) → user / admin の両 app で再利用可能
 *   - props `fallback?: (error, reset) => ReactNode` を追加 (custom UI 注入可能)
 *   - 「ホームへ」link (`/`) 追加
 *   - `console.error` 出力は維持。Sentry 連携は呼び側で `captureException` を import して fallback prop 経由で行う
 *     (shared-ui は apps/* に直接依存できないため、Sentry stub は cross-cut せず deps を切る)
 */

export interface PageErrorBoundaryProps {
  children: ReactNode;
  /** custom fallback (省略時は default UI)。`reset()` を呼ぶと error state がクリアされ再 render を試みる。 */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** default UI のタイトル (省略時は「ページの表示中にエラーが発生しました」)。 */
  fallbackTitle?: string;
  /** componentDidCatch の hook (Sentry 連携等、apps/* 側で注入)。 */
  onError?: (error: Error, errorInfo: { componentStack?: string | null }) => void;
}

interface State {
  error: Error | null;
}

export class PageErrorBoundary extends Component<PageErrorBoundaryProps, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, errorInfo: { componentStack?: string | null }): void {
    // eslint-disable-next-line no-console
    console.error('PageErrorBoundary caught', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  private reset = () => {
    this.setState({ error: null });
  };

  override render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) {
        return this.props.fallback(error, this.reset);
      }
      return (
        <div className="min-h-screen bg-slate-50 p-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white border border-error-300 rounded-lg p-4 shadow">
              <h1 className="text-base font-bold text-danger-700 mb-2">
                ⚠ {this.props.fallbackTitle ?? 'ページの表示中にエラーが発生しました'}
              </h1>
              <p className="text-sm text-slate-700 mb-3">
                ブラウザを再読み込み（Ctrl+Shift+R / Cmd+Shift+R）して再試行してください。
                繰り返し発生する場合は管理者にこの画面のスクリーンショットを送ってください。
              </p>
              <details className="bg-danger-50 border border-error-300 rounded p-2 text-xs">
                <summary className="cursor-pointer text-danger-700 font-semibold">
                  エラー詳細（管理者に報告）
                </summary>
                <div className="mt-2 space-y-1">
                  <div>
                    <strong>名前:</strong> <code className="bg-white px-1">{error.name}</code>
                  </div>
                  <div>
                    <strong>メッセージ:</strong>
                    <pre className="bg-white p-2 mt-1 whitespace-pre-wrap break-words">
                      {error.message}
                    </pre>
                  </div>
                  {error.stack && (
                    <div>
                      <strong>スタック:</strong>
                      <pre className="bg-white p-2 mt-1 whitespace-pre-wrap break-words text-[10px] max-h-64 overflow-auto">
                        {error.stack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={this.reset}
                  className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
                >
                  再試行
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (typeof window !== 'undefined') window.location.reload();
                  }}
                  className="px-3 py-1 text-sm bg-slate-200 text-slate-800 rounded hover:bg-slate-300"
                >
                  再読み込み
                </button>
                <a
                  href="/"
                  className="px-3 py-1 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 no-underline"
                >
                  ホームへ
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
